import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Segmented, message } from 'antd';
import styled from 'styled-components';
import ReadOnlyDocumentViewer from '../Document/ReadOnlyDocumentViewer';
import {
  BOX_TYPES,
  boxAnnotationsForSigner,
  boxLabel,
  boxPlacementAt,
  createBoxAnnotation,
  placedBoxAnnotations,
  readPlacedBoxes,
  resyncBoxesToRecipients,
  restorePlacedBoxes,
  selectBoxSoon,
  signerColor,
} from './esignBoxes';
import { isValidEmail } from '../../utils/ESignModalValidation';
import { pagePointAtClient, startBoxDrag } from './boxDrag';

const Layout = styled.div`
  display: flex;
  gap: 16px;
  height: 78vh;
`;

const Panel = styled.aside`
  width: 260px;
  flex-shrink: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Viewer = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const SignerRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
  margin-bottom: 6px;
`;

const SignerChip = styled.button`
  display: block;
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid ${({ $color }) => $color};
  background: ${({ $active, $color }) => ($active ? $color : '#fff')};
  color: ${({ $active, $color }) => ($active ? '#fff' : $color)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SignerTools = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  .ant-btn {
    height: 14px;
    width: 22px;
    padding: 0;
    font-size: 10px;
    line-height: 1;
    color: #6b7280;
  }
`;

const AddPersonForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px dashed #c7d2fe;
  border-radius: 6px;
  background: #f8faff;
`;

const FormError = styled.div`
  font-size: 11px;
  color: #c63939;
`;

const DELIVERY_OPTIONS = [
  { label: 'Email', value: 'EMAIL' },
  { label: 'Text', value: 'SMS' },
  { label: 'Both', value: 'BOTH' },
];

const emptyPerson = () => ({ name: '', email: '', phone: '', delivery_method: 'EMAIL' });

const personProblem = ({ name, email, phone, delivery_method }) => {
  if (!name.trim()) return 'Name is required.';
  if (!email.trim()) return 'Email is required — the signing link is sent there.';
  if (!isValidEmail(email.trim())) return 'That email address does not look right.';
  if ((delivery_method === 'SMS' || delivery_method === 'BOTH') && !phone.trim()) {
    return 'A phone number is needed to send a text.';
  }
  return '';
};

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;

  .ant-btn {
    cursor: grab;
    touch-action: none;
    user-select: none;
  }
`;

const BoxRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #f3f4f6;
  color: ${({ $color }) => $color};
`;

const Help = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const PlaceSignatureBoxes = ({
  open, document, recipients, initialBoxes, onCancel, onDone, resolveSource, title, signerHeading,
  onAddRecipient, onRemoveRecipient, onMoveRecipient,
}) => {
  const [instance, setInstance] = useState(null);
  const [activeSigner, setActiveSigner] = useState(1);
  const [boxes, setBoxes] = useState([]);
  const [newPerson, setNewPerson] = useState(null);
  const [newPersonError, setNewPersonError] = useState('');
  const restoredRef = useRef(false);
  const canEditPeople = Boolean(onAddRecipient);

  const refresh = useCallback((inst) => {
    setBoxes(
      placedBoxAnnotations(inst).map((annotation) => {
        const data = JSON.parse(annotation.getCustomData('whgEsignBox'));
        return { annotation, ...data, page: annotation.PageNumber };
      })
    );
  }, []);

  const handleInstanceReady = useCallback((inst) => {
    inst.Core.annotationManager.disableReadOnlyMode?.();
    const editTool = inst.Core.Tools?.ToolNames?.EDIT || 'AnnotationEdit';
    inst.UI.setToolMode?.(editTool);
    setInstance(inst);
  }, []);

  // Each opening boots a fresh viewer; forget the last one so Edit restores
  // the placed boxes into the new viewer and Done never reads a disposed one.
  useEffect(() => {
    if (open) return;
    setInstance(null);
    setBoxes([]);
    setNewPerson(null);
    setNewPersonError('');
    restoredRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!instance) return undefined;
    if (!restoredRef.current) {
      restoredRef.current = true;
      restorePlacedBoxes(instance, initialBoxes, recipients);
    }
    refresh(instance);
    const onChange = () => refresh(instance);
    instance.Core.annotationManager.addEventListener('annotationChanged', onChange);
    return () => instance.Core.annotationManager.removeEventListener('annotationChanged', onChange);
  }, [instance, initialBoxes, recipients, refresh]);

  // People can be added, removed or reordered while boxes are already on the
  // page: renumber and recolour the boxes so they keep following their signer.
  useEffect(() => {
    if (!instance || !restoredRef.current) return;
    resyncBoxesToRecipients(instance, recipients);
    refresh(instance);
  }, [instance, recipients, refresh]);

  useEffect(() => {
    if (activeSigner > recipients.length) setActiveSigner(Math.max(1, recipients.length));
  }, [activeSigner, recipients.length]);

  const startAddingPerson = () => {
    setNewPerson(emptyPerson());
    setNewPersonError('');
  };

  const changeNewPerson = (field, value) => {
    setNewPerson((person) => ({ ...person, [field]: value }));
    setNewPersonError('');
  };

  const commitNewPerson = () => {
    const problem = personProblem(newPerson);
    if (problem) {
      setNewPersonError(problem);
      return;
    }
    const duplicate = recipients.some(
      (recipient) => (recipient.email || '').trim().toLowerCase() === newPerson.email.trim().toLowerCase()
    );
    if (duplicate) {
      setNewPersonError('Someone with that email is already on the list.');
      return;
    }
    onAddRecipient({
      name: newPerson.name.trim(),
      email: newPerson.email.trim(),
      phone: newPerson.phone.trim(),
      delivery_method: newPerson.delivery_method,
    });
    setActiveSigner(recipients.length + 1);
    setNewPerson(null);
  };

  const removePerson = (index) => {
    if (instance) {
      boxAnnotationsForSigner(instance, recipients[index], index + 1)
        .forEach((annotation) => instance.Core.annotationManager.deleteAnnotation(annotation));
    }
    onRemoveRecipient(index);
  };

  const movePerson = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= recipients.length) return;
    onMoveRecipient(index, target);
    if (activeSigner === index + 1) setActiveSigner(target + 1);
    else if (activeSigner === target + 1) setActiveSigner(index + 1);
  };

  const addBox = (type, placement = null) => {
    if (!instance) return;
    const recipient = recipients[activeSigner - 1] || {};
    const annotation = createBoxAnnotation(instance, {
      type,
      signerPosition: activeSigner,
      signerEmail: recipient.email,
      signerName: recipient.name,
    }, placement);
    selectBoxSoon(instance, annotation);
  };

  const dropBox = (type, clientX, clientY) => {
    const point = pagePointAtClient(instance, clientX, clientY);
    if (!point) {
      message.info('Drop the box on the page where it should go.');
      return;
    }
    addBox(type, boxPlacementAt(instance, type, point));
  };

  const beginDrag = (type, event) => {
    if (!instance) return;
    startBoxDrag(event, {
      label: boxLabel(type, recipients[activeSigner - 1]?.name),
      color: signerColor(activeSigner),
      onDrop: (x, y) => dropBox(type, x, y),
    });
  };

  const removeBox = (annotation) => {
    instance?.Core.annotationManager.deleteAnnotation(annotation);
  };

  const handleDone = () => {
    onDone(instance ? readPlacedBoxes(instance) : []);
  };

  return (
    <Modal
      open={open}
      title={title}
      width="95vw"
      style={{ top: 16 }}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="done" type="primary" onClick={handleDone} disabled={!instance}>
          Done — {boxes.length} box{boxes.length === 1 ? '' : 'es'}
        </Button>,
      ]}
    >
      <Layout>
        <Panel>
          <div>
            <SectionTitle>{signerHeading}</SectionTitle>
            {recipients.map((recipient, index) => (
              <SignerRow key={`${recipient.email}-${index}`}>
                <SignerChip
                  type="button"
                  $color={signerColor(index + 1)}
                  $active={activeSigner === index + 1}
                  onClick={() => setActiveSigner(index + 1)}
                  title={recipient.email}
                  data-testid={`esign-signer-${index + 1}`}
                >
                  {index + 1}. {recipient.name || recipient.email || `Signer ${index + 1}`}
                </SignerChip>
                {canEditPeople && (
                  <SignerTools>
                    <Button
                      type="text"
                      size="small"
                      aria-label={`Move ${recipient.name || 'signer'} up`}
                      disabled={index === 0}
                      onClick={() => movePerson(index, -1)}
                    >
                      ▲
                    </Button>
                    <Button
                      type="text"
                      size="small"
                      aria-label={`Move ${recipient.name || 'signer'} down`}
                      disabled={index === recipients.length - 1}
                      onClick={() => movePerson(index, 1)}
                    >
                      ▼
                    </Button>
                  </SignerTools>
                )}
                {canEditPeople && (
                  <Button
                    type="text"
                    size="small"
                    aria-label={`Remove ${recipient.name || 'signer'}`}
                    disabled={recipients.length <= 1}
                    onClick={() => removePerson(index)}
                    style={{ color: '#9ca3af', padding: '0 4px' }}
                  >
                    ✕
                  </Button>
                )}
              </SignerRow>
            ))}
            {canEditPeople && !newPerson && (
              <Button
                size="small"
                type="dashed"
                block
                onClick={startAddingPerson}
                data-testid="esign-add-person"
              >
                + Add person
              </Button>
            )}
            {canEditPeople && newPerson && (
              <AddPersonForm data-testid="esign-add-person-form">
                <Input
                  size="small"
                  placeholder="Name"
                  value={newPerson.name}
                  onChange={(e) => changeNewPerson('name', e.target.value)}
                  onPressEnter={commitNewPerson}
                  autoFocus
                  data-testid="esign-new-person-name"
                />
                <Input
                  size="small"
                  placeholder="Email"
                  type="email"
                  value={newPerson.email}
                  onChange={(e) => changeNewPerson('email', e.target.value)}
                  onPressEnter={commitNewPerson}
                  data-testid="esign-new-person-email"
                />
                <Input
                  size="small"
                  placeholder="Phone (optional)"
                  value={newPerson.phone}
                  onChange={(e) => changeNewPerson('phone', e.target.value)}
                  onPressEnter={commitNewPerson}
                  data-testid="esign-new-person-phone"
                />
                <Segmented
                  size="small"
                  options={DELIVERY_OPTIONS}
                  value={newPerson.delivery_method}
                  onChange={(value) => changeNewPerson('delivery_method', value)}
                />
                {newPersonError && <FormError role="alert">{newPersonError}</FormError>}
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button size="small" type="primary" onClick={commitNewPerson} data-testid="esign-new-person-add">
                    Add
                  </Button>
                  <Button size="small" onClick={() => setNewPerson(null)}>Cancel</Button>
                </div>
              </AddPersonForm>
            )}
            {canEditPeople && (
              <Help style={{ marginTop: 6 }}>
                People sign in this order, one after another. Use ▲▼ to change it.
              </Help>
            )}
          </div>
          <div>
            <SectionTitle>2. Add a box</SectionTitle>
            <TypeGrid>
              {BOX_TYPES.map(({ type, label }) => (
                <Button
                  key={type}
                  onPointerDown={(event) => beginDrag(type, event)}
                  onClick={() => addBox(type)}
                  disabled={!instance}
                  data-testid={`esign-box-type-${type}`}
                >
                  {label}
                </Button>
              ))}
            </TypeGrid>
          </div>
          <Help>
            Drag a box onto the line where it goes, then pull a corner to resize it. Clicking a box
            instead drops it in the middle of the page. Select a box and press Delete to remove it.
          </Help>
          <div>
            <SectionTitle>Placed boxes</SectionTitle>
            {boxes.length === 0 && <Help>None yet.</Help>}
            {boxes.map((box, index) => (
              <BoxRow key={box.annotation.Id || index} $color={signerColor(box.signer_position)}>
                <span>
                  {boxLabel(box.field_type, recipients[box.signer_position - 1]?.name)} · p.{box.page}
                </span>
                <Button size="small" type="link" onClick={() => removeBox(box.annotation)}>
                  Remove
                </Button>
              </BoxRow>
            ))}
          </div>
        </Panel>
        <Viewer>
          {open && (
            <ReadOnlyDocumentViewer
              documentId={document?.id}
              filename={document?.name}
              fileType="pdf"
              onInstanceReady={handleInstanceReady}
              resolveSource={resolveSource}
            />
          )}
        </Viewer>
      </Layout>
    </Modal>
  );
};

PlaceSignatureBoxes.propTypes = {
  open: PropTypes.bool.isRequired,
  document: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), name: PropTypes.string }),
  recipients: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, email: PropTypes.string })).isRequired,
  initialBoxes: PropTypes.arrayOf(PropTypes.object),
  onCancel: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  resolveSource: PropTypes.func,
  title: PropTypes.string,
  signerHeading: PropTypes.string,
  // When all three are given, people can be added, removed and reordered
  // right here (the way Formstack does it) instead of only in the send window.
  onAddRecipient: PropTypes.func,
  onRemoveRecipient: PropTypes.func,
  onMoveRecipient: PropTypes.func,
};

PlaceSignatureBoxes.defaultProps = {
  document: null,
  initialBoxes: [],
  resolveSource: null,
  title: 'Place signature boxes',
  signerHeading: '1. Who signs this box',
  onAddRecipient: null,
  onRemoveRecipient: null,
  onMoveRecipient: null,
};

export default PlaceSignatureBoxes;
